"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PropertyManagementExitSolutions() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="380" id="property-management-exit-solutions" className="dark bg-background text-white relative overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen lg:min-h-[90vh]">
        {}
        <div ref={leftRef} className={`order-2 lg:order-1 lg:col-span-5 relative flex flex-col justify-center px-6 sm:px-10 lg:px-14 py-16 lg:py-20 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div aria-hidden="true" className="pointer-events-none absolute -top-20 -left-20 w-72 h-72 rounded-md opacity-10 blur-3xl" style={{
          background: "radial-gradient(circle at center, var(--primary), transparent 70%)"
        }} />

          {}
          <div className="relative z-10 mb-8 lg:mb-10">
            <span className="font-mono text-[11px] sm:text-xs tracking-[0.25em] uppercase font-semibold" style={{
            color: "white"
          }}>Est. 1997 · 29 Yrs Resort Expertise</span>
            <span className="inline-block w-12 h-px ml-4 align-middle" style={{
            background: "var(--primary)"
          }} />
          </div>

          {}
          <h1 className="font-serif font-bold text-white relative z-10" style={{
          fontSize: "clamp(2.5rem, 8vw, 5rem)",
          lineHeight: "1.1",
          letterSpacing: "-0.04em"
        }}>
            Architects of Asset
            <br />
            Liberation &{" "}
            <em className="italic font-medium" style={{
            color: "white"
          }}>
              Luxury
            </em>
            .
          </h1>

          {}
          <p className="mt-8 lg:mt-10 text-lg leading-relaxed text-white max-w-[380px] relative z-10" style={{
          fontFamily: "var(--font-sans)"
        }}>
            Elite property management and legal timeshare exit solutions for 
            discerning owners in Los Cabos and beyond.
          </p>

          {}
          <div className="mt-10 lg:mt-12 flex flex-col sm:flex-row gap-4 relative z-10">
            <Button asChild size="lg" className="rounded-md px-7 h-12 bg-primary text-primary-foreground hover:bg-foreground hover:text-background transition-colors duration-200 font-default font-semibold tracking-wide">
              <Link to="https://imielite.com/contact/">Manage Your Property</Link>
            </Button>

            <Button asChild variant="ghost" size="lg" className="group rounded-md px-7 h-12 border border-border bg-transparent text-white hover:border-primary hover:text-primary font-default font-semibold tracking-wide">
              <Link to="https://imielite.com/timeshare-exit-strategy/">
                Exit Your Timeshare
                <ArrowRight className="ml-1 size-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </Button>
          </div>

          {}
          <div className="mt-14 lg:mt-20 flex items-end gap-3 relative z-10">
            <svg width="44" height="56" viewBox="0 0 44 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="opacity-60 shrink-0 text-primary">
              <path d="M22 54 C 22 40, 22 26, 22 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" />
              <path d="M6 20 L 22 14 L 38 20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" fill="none" />
              <path d="M9 41 L 22 36 L 35 41" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" fill="none" />
            </svg>
            <p className="text-sm text-white font-mono leading-snug max-w-[220px]">
              PROFECO-compliant & SAT registered operations.
            </p>
          </div>
        </div>

        {}
        <div ref={rightRef} className={`order-1 lg:order-2 lg:col-span-7 relative min-h-[60vh] lg:min-h-full overflow-hidden transition-all duration-1000 ease-out ${rightVisible ? "opacity-100 scale-100" : "opacity-0 scale-105"}`}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/cabo_arch_aerial-scaled.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/cabo_arch_aerial-scaled.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/cabo_arch_aerial-scaled.png-wvc-sizes" alt="Aerial architectural view of the iconic arches in Cabo San Lucas" loading="eager" decoding="async" className="absolute inset-0 w-full h-full object-cover grayscale-[20%] contrast-[1.1]" />
          {}
          <div aria-hidden="true" className="absolute inset-0 lg:hidden" style={{
          background: "linear-gradient(to bottom, transparent 60%, var(--background) 100%)"
        }} />
        </div>

        {}
        <div className={`hidden lg:flex absolute z-20 top-1/2 -translate-y-1/2 items-center justify-center transition-all duration-1000 delay-300 ${leftVisible ? "opacity-100 rotate-[-8deg] scale-100" : "opacity-0 rotate-0 scale-75"}`} style={{
        left: "41.666%",
        transform: "translate(-50%, -50%) rotate(-8deg)"
      }}>
          <div className="relative w-32 h-32 xl:w-36 xl:h-36 rounded-md flex items-center justify-center shadow-lg" style={{
          background: "var(--primary)",
          borderRadius: "0px"
        }}>
            {}
            <div className="absolute inset-2 border border-background/30" style={{
            borderRadius: "0px"
          }} />
            <div className="text-center px-4">
              <p className="font-mono font-bold text-[10px] xl:text-[11px] leading-tight" style={{
              color: "var(--background)"
            }}>
                ELITE
                <br />
                <span className="not-italic text-[8px] tracking-widest">·</span>
                <br />
                COMPLIANT
                <br />
                <span className="not-italic text-[8px] tracking-widest">·</span>
                <br />
                SECURE
              </p>
            </div>
          </div>
        </div>

        {}
        <div className="lg:hidden absolute z-20 top-6 right-6">
          <div className="relative w-24 h-24 flex items-center justify-center shadow-lg rotate-[-8deg]" style={{
          background: "var(--primary)",
          borderRadius: "0px"
        }}>
            <div className="absolute inset-1.5 border border-background/30" style={{
            borderRadius: "0px"
          }} />
            <div className="text-center px-2">
              <p className="font-mono font-bold text-[9px] leading-tight" style={{
              color: "var(--background)"
            }}>
                ELITE
                <br />
                COMPLIANT
                <br />
                SECURE
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}